import { createContext, useContext, useEffect, useMemo } from "react";
import { resolveBrand, DEFAULT_BRAND } from "@/brands";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Iter 43 — BrandProvider.
 *
 * Reads `user.brand` and injects CSS custom properties on <html>:
 *   --brand-accent, --brand-accent-soft, --brand-gradient-from,
 *   --brand-gradient-to, --brand-logo-url
 *
 * Consumers can read the resolved brand object via `useBrand()`.
 * Falls back to Whiplash for logged-out users / brand=null so the
 * marketing surfaces (login, share view) always render on-brand.
 */
const BrandContext = createContext({ brand: resolveBrand(DEFAULT_BRAND) });

export function BrandProvider({ children }) {
  const { user } = useAuth();
  const brand = useMemo(() => resolveBrand(user?.brand || DEFAULT_BRAND), [user?.brand]);

  useEffect(() => {
    const r = document.documentElement;
    r.style.setProperty("--brand-accent", brand.accent);
    r.style.setProperty("--brand-accent-soft", brand.accentSoft);
    r.style.setProperty("--brand-gradient-from", brand.gradientFrom);
    r.style.setProperty("--brand-gradient-to", brand.gradientTo);
    r.dataset.brand = brand.id;
  }, [brand]);

  return (
    <BrandContext.Provider value={{ brand }}>
      {children}
    </BrandContext.Provider>
  );
}

export function useBrand() {
  return useContext(BrandContext).brand;
}
